/**
 * « Un sujet entier à faire » — sous la carte des entraînements.
 *
 * Hedi, le 26 septembre 2026 : un sujet d'annales, donné par un parent depuis
 * le recueil Prépabac, se fait **en entier, sur papier, en temps limité**. Ce
 * n'est pas une série d'exercices : il n'y a pas une photo par exercice mais
 * autant de pages de copie qu'il en faut, et la copie se **rend** d'un geste,
 * une fois toutes les pages photographiées.
 *
 * **Le temps est annoncé, pas chronométré.** L'écran dit le temps conseillé par
 * l'éditeur ; il ne lance rien, ne compte rien et n'enregistre aucune durée —
 * aucun chiffre sur la performance de l'enfant (§14.1). Quand l'éditeur ne donne
 * pas de durée, l'écran n'en invente pas.
 *
 * **Rendre est le critère d'achèvement, pas le verdict.** Une copie rendue
 * quitte cet écran ; le parent la juge avec le corrigé de l'éditeur, et le
 * verdict ne revient pas ici.
 *
 * **Le corrigé ne traverse jamais cette route** (règle 2) : le schéma de
 * l'enfant n'a pas de champ où le mettre, et un test l'affirme sur le texte HTTP
 * brut.
 *
 * **Le sujet de bac du dimanche** (lycée, `web-pwa` §17.12) suit les mêmes
 * gestes, avec trois différences. L'élève **choisit l'heure** — pas le principe :
 * le sujet est prévu, rien n'invite à le repousser ni ne commente le choix.
 * **L'énoncé n'arrive qu'au départ du chronomètre**, que tient le parent : le
 * serveur ne le rend pas avant, la copie ne se photographie pas avant. Et une
 * fois le sujet noté au barème, la note sur 20 revient ici — au lycée l'élève
 * voit ses propres chiffres ; au collège la réponse n'en porte pas.
 *
 * La confirmation du rendu est rendue dans la page, jamais par `window.confirm`
 * (refus silencieux dans la coquille Android).
 */

import { useCallback, useEffect, useState } from "react";

import { call } from "@/api/client";
import { ApiError } from "@/api/errors";
import type { MaNoteDeSujetOut, MonSujetOut } from "@/api/schema";
import { uploadPhoto } from "@/photo/upload";
import { EnonceImage, imageDeLEnonce } from "@/ui/EnonceImage";
import { etatDuChronometre, heureLisible, ligneDuChronometre } from "@/ui/chronometre";
import { enClairCourt } from "@/ui/dates";
import { EtiquetteParascolaire } from "@/ui/Parascolaire";
import { Button, Card, ErrorNote } from "@/ui/primitives";

type Etat =
  | { quoi: "chargement" }
  | { quoi: "panne" }
  | { quoi: "pret"; sujets: MonSujetOut[]; notes: MaNoteDeSujetOut[] };

/** « 13,5 / 20 ». */
function surVingt(note: number): string {
  return `${String(Math.round(note * 100) / 100).replace(".", ",")} / 20`;
}

function tempsConseille(minutes: number | null | undefined): string | null {
  if (minutes == null) return null;
  if (minutes < 60) return `${minutes} minutes`;
  const heures = Math.floor(minutes / 60);
  const reste = minutes % 60;
  const h = `${heures} heure${heures > 1 ? "s" : ""}`;
  return reste ? `${h} ${reste}` : h;
}

export function SujetsDuParent() {
  const [etat, setEtat] = useState<Etat>({ quoi: "chargement" });
  const [rendu, setRendu] = useState(false);

  const charger = useCallback(async () => {
    try {
      const reponse = await call("mesSujets");
      setEtat({ quoi: "pret", sujets: reponse.sujets ?? [], notes: reponse.notes ?? [] });
    } catch {
      setEtat({ quoi: "panne" });
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  if (etat.quoi === "chargement") return null;

  if (etat.quoi === "panne") {
    return (
      <Card title="Un sujet entier à faire">
        <ErrorNote>Je n'arrive pas à lire le sujet préparé pour toi.</ErrorNote>
        <Button variant="quiet" onClick={() => void charger()}>
          Réessayer
        </Button>
      </Card>
    );
  }

  const notes =
    etat.notes.length > 0 ? (
      <Card title="Tes sujets de bac notés">
        <ul className="list">
          {etat.notes.map((note) => (
            <li key={note.id} className="list__item">
              {note.titre}
              {note.dimanche ? ` · ${enClairCourt(note.dimanche)}` : null} :{" "}
              <strong>{surVingt(note.note_sur_20)}</strong>
            </li>
          ))}
        </ul>
      </Card>
    ) : null;

  if (etat.sujets.length === 0) {
    return (
      <>
        {rendu ? (
          <Card title="Un sujet entier à faire">
            <p className="muted small">
              Ta copie est rendue. Ton parent va la regarder avec la correction.
            </p>
          </Card>
        ) : null}
        {notes}
      </>
    );
  }

  return (
    <>
      {etat.sujets.map((sujet) => (
        <Sujet
          key={sujet.id}
          sujet={sujet}
          rafraichir={charger}
          quandRendu={() => setRendu(true)}
        />
      ))}
      {notes}
    </>
  );
}

type Envoi = "repos" | "envoi" | "panne";

function Sujet({
  sujet,
  rafraichir,
  quandRendu,
}: {
  sujet: MonSujetOut;
  rafraichir: () => Promise<void>;
  quandRendu: () => void;
}) {
  const [envoi, setEnvoi] = useState<Envoi>("repos");
  const [erreur, setErreur] = useState<string | null>(null);
  const [confirme, setConfirme] = useState(false);
  const photos = sujet.photos ?? [];
  const pages = sujet.enonce_urls ?? [];
  const temps = tempsConseille(sujet.duree_officielle_min ?? sujet.duree_min);
  const recueil = sujet.ouvrage_titre ?? "Recueil de sujets";
  const chrono = etatDuChronometre(sujet);
  // Le sujet du dimanche attend le chronomètre du parent : ni énoncé ni copie
  // avant. Un sujet donné à la main se fait quand l'élève veut.
  const attend = Boolean(sujet.dimanche) && chrono === "pas_demarre";
  const [heure, setHeure] = useState(sujet.heure_choisie?.slice(0, 5) ?? "");

  const agir = async (action: () => Promise<unknown>, apres?: () => void) => {
    setEnvoi("envoi");
    setErreur(null);
    try {
      await action();
      apres?.();
      await rafraichir();
      setEnvoi("repos");
    } catch (echec) {
      setErreur(echec instanceof ApiError ? echec.message : null);
      setEnvoi("panne");
    }
  };

  const photographier = (file: File) =>
    agir(async () => {
      const photo = await uploadPhoto(file);
      await call("photographierUnePageDeCopie", {
        params: { sujet_donne_id: sujet.id },
        body: { key: photo.key },
      });
    });

  const choisirLHeure = () =>
    agir(() =>
      call("choisirLHeureDuSujet", {
        params: { sujet_donne_id: sujet.id },
        body: { heure },
      }),
    );

  if (attend) {
    const quand = sujet.dimanche ? enClairCourt(sujet.dimanche) : "dimanche";
    const choisie = heureLisible(sujet.heure_choisie);
    return (
      <Card title="Le sujet de bac du dimanche">
        <p className="devoirs__entete">
          <strong>{sujet.titre}</strong>
          <span className="muted small"> · {sujet.chapitre_titre}</span>
        </p>
        <p className="muted small">
          Prévu {quand}, en conditions d'examen
          {temps ? ` : ${temps}` : null}. Ton parent lance le chronomètre ; l'énoncé
          s'affiche à ce moment-là.
        </p>
        <label className="devoirs__prise">
          <span className="muted small">
            {choisie ? `Tu as choisi ${choisie}. Tu peux changer :` : "À quelle heure ?"}
          </span>
          <input
            className="input"
            type="time"
            min="07:00"
            max="21:30"
            step={900}
            value={heure}
            disabled={envoi === "envoi"}
            onChange={(event) => setHeure(event.target.value)}
          />
        </label>
        <Button
          type="button"
          disabled={envoi === "envoi" || !heure}
          onClick={() => void choisirLHeure()}
        >
          Choisir cette heure
        </Button>
        {envoi === "panne" ? (
          <ErrorNote>{erreur ?? "Ça n'est pas parti. Réessaie."}</ErrorNote>
        ) : null}
      </Card>
    );
  }

  const ligne = sujet.dimanche ? ligneDuChronometre(sujet, new Date()) : null;

  return (
    <Card title={sujet.dimanche ? "Le sujet de bac du dimanche" : "Un sujet entier à faire"}>
      <p className="devoirs__entete">
        <strong>{sujet.titre}</strong>
        <span className="muted small">
          {" "}
          · {sujet.chapitre_titre}
          {sujet.page != null ? ` · page ${sujet.page}` : null}
        </span>
      </p>
      <p className="muted small">
        Fais-le en entier sur une copie, sans regarder la correction.
        {temps && !sujet.dimanche ? ` Temps conseillé : ${temps}.` : null}
      </p>
      {ligne ? <p className="muted small">{ligne}</p> : null}

      {pages.map((url) => {
        const image = imageDeLEnonce(url);
        return image ? (
          <div key={url}>
            <EtiquetteParascolaire nom={recueil} />
            <EnonceImage image={image} statement="" />
          </div>
        ) : null;
      })}

      {photos.length > 0 ? (
        <ul className="devoirs__photos">
          {photos.map((photo, rang) => (
            <li key={photo.id}>
              {photo.url ? (
                <a href={photo.url} target="_blank" rel="noreferrer">
                  <img
                    className="devoirs__photo"
                    src={photo.url}
                    alt={`Page ${rang + 1} de ta copie`}
                    loading="lazy"
                  />
                </a>
              ) : null}
              <Button
                variant="quiet"
                disabled={envoi === "envoi"}
                onClick={() =>
                  void agir(() =>
                    call("retirerUnePageDeCopie", {
                      params: { sujet_donne_id: sujet.id, photo_id: photo.id },
                    }),
                  )
                }
              >
                Retirer la page {rang + 1}
              </Button>
            </li>
          ))}
        </ul>
      ) : null}

      <label className="devoirs__prise">
        <span className="muted small">
          {photos.length === 0
            ? "Photographie la première page de ta copie."
            : "Photographie la page suivante."}
        </span>
        <input
          className="input"
          type="file"
          accept="image/*"
          capture="environment"
          disabled={envoi === "envoi"}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (file) void photographier(file);
          }}
        />
      </label>

      {envoi === "envoi" ? <p className="muted small">Envoi…</p> : null}
      {envoi === "panne" ? (
        <ErrorNote>{erreur ?? "Ça n'est pas parti. Réessaie."}</ErrorNote>
      ) : null}

      {photos.length > 0 && !confirme ? (
        <Button type="button" disabled={envoi === "envoi"} onClick={() => setConfirme(true)}>
          Rendre ma copie
        </Button>
      ) : null}
      {photos.length > 0 && confirme ? (
        <div className="notice">
          <p>
            Rendre ta copie de {photos.length} page{photos.length > 1 ? "s" : ""} ? Ensuite, tu ne
            pourras plus y ajouter de page.
          </p>
          <div className="screen__actions">
            <Button
              type="button"
              disabled={envoi === "envoi"}
              onClick={() =>
                void agir(
                  () => call("rendreLaCopie", { params: { sujet_donne_id: sujet.id } }),
                  quandRendu,
                ).finally(() => setConfirme(false))
              }
            >
              Oui, rendre
            </Button>
            <Button type="button" variant="quiet" onClick={() => setConfirme(false)}>
              Pas encore
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}
